import React, { useState } from 'react'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import Popover from '@mui/material/Popover'
import Checkbox from '@mui/material/Checkbox'
import { useMediaQuery } from 'react-responsive'
import { mediaQueryForMobile } from '../../HomePage/HomePage'
import { DealModal } from '../../PricingListPage/DealModal'
import { useModalsStore } from '../../../stores/modalsStore'

const money = (v) => {
    const n = Number(v)
    if (v === null || v === undefined || v === '' || Number.isNaN(n)) return '—'
    return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

const NBSP = ' '

// Lender-program validity, matching the deal page's "This program is expired."
// warning (DealHeader) so a card never disagrees with the quote it opens:
// expired once the program's UTC date reaches today's. A missing or unparsable
// date yields false — an unknown program reads as Active, never as Expired.
const isProgramExpired = (expirationDate) => Boolean(
    expirationDate &&
    new Date(expirationDate).setUTCHours(0, 0, 0, 0) <= new Date().setUTCHours(0, 0, 0, 0)
)

// A pricing quote for the opportunity, rendered as a compact ListDealCard: the
// same header + deal-type-themed body structure and sizing. Deal types map
// LEASE→lease (blue), FINANCE→purchase (green), CASH→cash (purple), matching
// ListDealCard's body themes.
// Selection mode (AttachQuotesModal): with `selectable`, the card grows a
// checkbox that attaches/detaches in real time; `disabled` locks the checkbox
// while a request is in flight. Clicking the card body always previews the
// quote in DealModal (a new tab on mobile, where the modal is unusable);
// `onSaved` hears DealModal saves so the owner can refresh its data.
// Current Vehicles reuse the card with their own `deleteLabel` and drop the
// status dot entirely via `showStatus={false}` — a delivered car's program
// expiry says nothing useful. Everyone else gets Active/Expired derived from the
// quote's own program expiry; `statusLabel`/`statusExpired` still override it
// for anyone who has a better answer.
// `currentVehicle` (a sold car's inception/maturity dates) rides along to
// DealModal and puts the deal page in read-only historical mode. Only the WON
// opportunity cards pass it — quote cards leave it undefined and open the
// worksheet exactly as before.
// `actions` ([{label, onClick, danger}]) adds menu items above the delete one,
// which is how Current Vehicles offers "Extend Lease". It is purely additive:
// `onDelete`/`deleteLabel` behave exactly as they always have, and a caller that
// passes neither still gets no "⋯" at all.
// `vehicleBody` is Current Vehicles' one opt-in, and the whole of what makes
// this a vehicle card: the quote's amounts and stats give way to it, the header
// names the car (title, trim, VIN) instead of the lender, the MSRP slot stays
// put even when empty, and ownership nobody recorded reads as unknown rather
// than as this card's historical LEASE default. It is named for the one caller
// on purpose — those rules travel with it, so no future caller inherits them by
// passing a body of its own. Everyone else passes none and renders as ever.
export const QuoteCard = ({ quote, vehicleBody, onDelete, actions, onCardClick, currentVehicle, selectable, selected, disabled, onToggle, onSaved, showStatus = true, statusLabel, statusExpired, deleteLabel = 'Remove quote' }) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const openModal = useModalsStore((s) => s.openModal)
    // Derived, not defaulted: an explicit prop still wins, so a caller with a
    // better answer than the program window keeps overriding it.
    const expired = statusExpired ?? isProgramExpired(quote.expiration_date)
    const label = statusLabel ?? (expired ? 'Expired' : 'Active')
    // A quote whose type never arrived is still desked as a lease, as it always
    // was. A vehicle card is the one caller that can genuinely not know: a car
    // whose owner never said how they hold it is unknown, and drawing it in
    // lease blue would be an answer we don't have.
    const isUnknownType = !quote.deal_type && Boolean(vehicleBody)
    const dt = isUnknownType ? '' : (quote.deal_type || 'LEASE').toUpperCase()
    const isCash = dt === 'CASH'
    const isFinance = dt === 'FINANCE'
    const isPurchase = isCash || isFinance
    // A car the rep recorded as owned outright: no lender terms to show, so it
    // borrows cash's neutral styling, but unlike a purchase quote its odometer
    // still matters, so it keeps the MILES stat.
    const isOwned = dt === 'OWNED_OUTRIGHT'
    const noTerms = isCash || isOwned
    const bodyClass = isUnknownType ? 'unknown' : noTerms ? 'cash' : isFinance ? 'purchase' : 'lease'
    // A leased or financed car's label heads its expiration line.
    const dealTypeLabel = isOwned ? 'OWNED' : vehicleBody && (dt === 'LEASE' || isFinance) ? 'EXPIRATION' : dt
    const title = quote.description || 'New quote'
    const vin = String(quote.vin || '').toUpperCase()
    // Blank rather than "—": a car with no sticker price on record shows the
    // label alone, so its row still lines up with the cards beside it.
    const msrpValue = Number(quote.msrp) > 0 ? money(quote.msrp) : ''

    // The menu, in render order: the caller's own actions, then the delete that
    // was here before them. Delete stays keyed to `onDelete` alone, so a card
    // that offers an action but has nothing safe to delete shows the menu
    // without it — which is how a Current Vehicles sold card gets its menu.
    const menuItems = [
        ...(actions || []),
        ...(onDelete ? [{ label: deleteLabel, danger: true, onClick: () => onDelete(quote.id) }] : []),
    ]
    // Selection mode replaces the menu with a checkbox, as it always has.
    const hasMenu = !selectable && menuItems.length > 0

    const handleCardClick = () => {
        // A card can own its click — Current Vehicles' hand-recorded cars open
        // their own editor instead of a worksheet they don't have. Quote cards
        // pass nothing and keep the DealModal behavior below.
        if (onCardClick) { onCardClick(); return }
        if (!quote.id) return
        if (isTabletOrMobile) {
            window.open(`/quotes/${quote.id}`, '_blank')
            return
        }
        openModal({
            element: DealModal,
            id: 'DealModal',
            props: {
                id: quote.id,
                ...(currentVehicle && { currentVehicle }),
                onSave: (newDealState) => { if (onSaved) onSaved(newDealState) },
            },
        })
    }

    return (
        <div
            className={`cd-deal-card${selectable ? ' is-selectable' : ''}${selected ? ' is-selected' : ''}${disabled ? ' is-attached' : ''}`}
            onClick={handleCardClick}
        >
            {/* ── Top: header (panel bg) ────────────────────────── */}
            <div className="card-header">
                <div className="card-title-row">
                    {selectable && (
                        <Checkbox
                            className="select-check"
                            size="small"
                            checked={Boolean(selected)}
                            disabled={Boolean(disabled)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => { if (!disabled && onToggle) onToggle() }}
                            inputProps={{ 'aria-label': `Select ${title}` }}
                        />
                    )}
                    <div className="card-title-main">
                        <div className="card-title">{title}</div>
                        <div className="card-spec">{quote.spec || NBSP}</div>
                        {/* A vehicle card names the car it is about. The line
                            holds its place without a VIN, so the header keeps
                            the same shape either way. */}
                        {vehicleBody && <div className="card-spec">{vin ? `VIN ${vin}` : NBSP}</div>}
                    </div>
                    <div className="status-meta">
                        {showStatus && (
                            <div className={`status-dot${expired ? ' expired' : ''}`}>
                                <span className="dot" />
                                <span className="status-label">{label}</span>
                            </div>
                        )}
                        {hasMenu && <MoreHorizIcon
                            className="action-icon"
                            style={{ fontSize: 16 }}
                            onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget) }}
                        />}
                        {hasMenu && (
                            <Popover
                                open={Boolean(anchorEl)}
                                anchorEl={anchorEl}
                                onClose={() => setAnchorEl(null)}
                                onClick={(e) => e.stopPropagation()}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                {menuItems.map((item) => (
                                    <div
                                        key={item.label}
                                        className={`qcard-menu-item${item.danger ? ' is-danger' : ''}`}
                                        onClick={() => { setAnchorEl(null); item.onClick() }}
                                    >
                                        {item.label}
                                    </div>
                                ))}
                            </Popover>
                        )}
                    </div>
                </div>

                {/* Who financed it belongs to a quote; a vehicle card's header
                    is about the car alone. */}
                {!vehicleBody && quote.lender && <div className="card-subtitle">{quote.lender}</div>}
                {quote.note && <div className="card-note">{quote.note}</div>}
            </div>

            {/* ── Bottom: type-themed body ──────────────────────── */}
            <div className={`card-body ${bodyClass}`}>
                {/* Ownership and MSRP stay where this card has always drawn
                    them, whichever body follows. */}
                <div className="deal-type-row">
                    <span className="deal-type">{dealTypeLabel}</span>
                    {(vehicleBody || msrpValue) && <span className="msrp-label">MSRP {msrpValue || NBSP}</span>}
                </div>

                {vehicleBody}

                {!vehicleBody && <div className="amounts-row">
                    <div className="amount-left">
                        <div className="big">{noTerms ? 'N/A' : money(quote.due)}</div>
                        <div className="lbl">{isPurchase ? 'Down Payment' : 'Due at Signing'}</div>
                    </div>
                    <div className="amount-right">
                        <div className="big">{money(quote.payment)}</div>
                        <div className="lbl">{noTerms ? 'Payment' : '/ month'}</div>
                    </div>
                </div>}

                {!vehicleBody && <div className="stats-row">
                    <div className="stat">
                        <div className="val">{noTerms ? 'N/A' : (quote.term || '—')}</div>
                        <div className="key">MONTHS</div>
                    </div>
                    {!isPurchase && (
                        <div className="stat">
                            <div className="val">{quote.miles ? Number(quote.miles).toLocaleString() : '—'}</div>
                            <div className="key">MILES</div>
                        </div>
                    )}
                    <div className="stat">
                        <div className="val">{quote.profit ? money(quote.profit) : '—'}</div>
                        <div className="key">PROFIT</div>
                    </div>
                </div>}
            </div>
        </div>
    )
}
